import { useState, useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useFullProfile } from '@/hooks/useFullProfile';
import { useI18n } from '@/hooks/useI18n';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import AutocompleteSelect from '@/components/ui/autocomplete-select';
import { Palette, User, Globe, Award, Languages, Heart, X, MapPin, Sparkles, Camera } from 'lucide-react';
import { toast } from '@/hooks/useToast';
import ImageUpload from '@/components/ui/ImageUpload';
import { BrandPageSkeleton } from './BrandPageSkeleton';
import { TRAVEL_INTERESTS } from '@/constants/travel-interests';
import { COUNTRIES, getCountryByCode } from '@/constants/countries';

const BrandPage = () => {
  const { userData, refreshUserData } = useAuth();
  const { t } = useI18n();
  const { profileData, profileLoading, invalidateProfile } = useFullProfile();

  const [agencyId, setAgencyId] = useState<string | null>(null);

  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoSmallUrl, setLogoSmallUrl] = useState<string | null>(null);

  // Professional info
  const [specialization, setSpecialization] = useState('');
  const [yearsExperience, setYearsExperience] = useState('');
  const [bio, setBio] = useState('');

  // New refactored fields
  const [headline, setHeadline] = useState('');
  const [destinationCountries, setDestinationCountries] = useState<string[]>([]);
  const [newCountrySearch, setNewCountrySearch] = useState('');
  const [yearsExperienceInt, setYearsExperienceInt] = useState<number>(0);
  const [location, setLocation] = useState('');
  const [gallery, setGallery] = useState<string[]>([]);
  const [travelInterests, setTravelInterests] = useState<string[]>([]);

  // Arrays (legacy)
  const [interests, setInterests] = useState<string[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [newLanguage, setNewLanguage] = useState('');
  const [certifications, setCertifications] = useState<string[]>([]);
  const [newCertification, setNewCertification] = useState('');

  // Social links
  const [socialInstagram, setSocialInstagram] = useState('');
  const [socialLinkedin, setSocialLinkedin] = useState('');
  const [socialWebsite, setSocialWebsite] = useState('');
  const [socialFacebook, setSocialFacebook] = useState('');

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const pp = profileData?.public_profile;
    if (!pp) return;
    setLogoUrl(pp.logo_url || null);
    setLogoSmallUrl(pp.logo_small_url || null);
    setAgencyId(pp.agency_id || null);
    setSpecialization(pp.specialization || '');
    setYearsExperience(pp.years_experience || '');
    setBio(pp.bio || '');
    setInterests(pp.interests || []);
    setLanguages(pp.languages || []);
    setCertifications(pp.certifications || []);
    setHeadline(pp.headline || '');
    setDestinationCountries(pp.destination_countries || []);
    setYearsExperienceInt(pp.years_experience_int || 0);
    setLocation(pp.location || '');
    setGallery(pp.gallery || []);
    setTravelInterests(pp.travel_interests || []);
    const social = pp.social_links || {};
    setSocialInstagram(social.instagram || '');
    setSocialLinkedin(social.linkedin || '');
    setSocialWebsite(social.website || '');
    setSocialFacebook(social.facebook || '');
  }, [profileData]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const socialLinks = {
        ...(socialInstagram && { instagram: socialInstagram }),
        ...(socialLinkedin && { linkedin: socialLinkedin }),
        ...(socialWebsite && { website: socialWebsite }),
        ...(socialFacebook && { facebook: socialFacebook }),
      };

      if (gallery.length > 4) {
        toast({ title: t('brand.messages.error'), description: t('brand.gallery.maxImagesError'), variant: 'destructive' });
        return;
      }

      const { error } = await supabase.rpc('update_my_public_profile' as any, {
        p_specialization: specialization || null,
        p_years_experience: yearsExperience || null,
        p_bio: bio || null,
        p_interests: interests.length > 0 ? interests : [],
        p_languages: languages.length > 0 ? languages : [],
        p_certifications: certifications.length > 0 ? certifications : [],
        p_social_links: Object.keys(socialLinks).length > 0 ? socialLinks : {},
        // New refactored fields
        p_headline: headline || null,
        p_destination_countries: destinationCountries.length > 0 ? destinationCountries : [],
        p_travel_interests: travelInterests.length > 0 ? travelInterests : [],
        p_gallery: gallery.length > 0 ? gallery : [],
        p_location: location || null,
      });

      if (error) throw error;

      invalidateProfile();
      await refreshUserData();
      toast({ title: t('brand.messages.profileUpdated'), description: t('brand.messages.profileUpdatedDesc') });
    } catch (err: any) {
      toast({ title: t('brand.messages.saveError'), description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const toggleTravelInterest = (interestId: string) => {
    if (travelInterests.includes(interestId)) {
      setTravelInterests(travelInterests.filter(id => id !== interestId));
    } else {
      setTravelInterests([...travelInterests, interestId]);
    }
  };

  const addDestinationCountry = (countryCode: string) => {
    if (!destinationCountries.includes(countryCode)) {
      setDestinationCountries([...destinationCountries, countryCode]);
      setNewCountrySearch('');
    }
  };

  const removeDestinationCountry = (countryCode: string) => {
    setDestinationCountries(destinationCountries.filter(c => c !== countryCode));
  };

  const addLanguage = () => {
    if (newLanguage.trim() && !languages.includes(newLanguage.trim())) {
      setLanguages([...languages, newLanguage.trim()]);
      setNewLanguage('');
    }
  };

  const removeLanguage = (item: string) => {
    setLanguages(languages.filter(l => l !== item));
  };

  const addCertification = () => {
    if (newCertification.trim() && !certifications.includes(newCertification.trim())) {
      setCertifications([...certifications, newCertification.trim()]);
      setNewCertification('');
    }
  };

  const removeCertification = (item: string) => {
    setCertifications(certifications.filter(c => c !== item));
  };

  const handleGalleryUpload = async (file: File, index: number) => {
    if (!userData?.id) return;
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `users/${userData.id}/gallery/${index}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('p2b')
        .upload(path, file, { upsert: true, contentType: file.type });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from('p2b').getPublicUrl(path);
      const imageUrl = `${urlData.publicUrl}?t=${Date.now()}`;

      const newGallery = [...gallery];
      newGallery[index] = imageUrl;
      setGallery(newGallery);

      return imageUrl;
    } catch (err: any) {
      toast({ title: t('brand.messages.uploadError'), description: err.message, variant: 'destructive' });
      throw err;
    }
  };

  const removeGalleryImage = (index: number) => {
    // Remove from state - the file will remain in storage until save
    setGallery(gallery.filter((_, i) => i !== index));
  };

  const handleLogoUpload = async (file: File): Promise<string> => {
    if (!userData?.id) throw new Error('User ID not available');
    try {
      const ext = file.name.split('.').pop() || 'png';
      const path = `users/${userData.id}/logo.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('p2b')
        .upload(path, file, { upsert: true, contentType: file.type });

      if (uploadError) throw new Error(uploadError.message);

      const { data: urlData } = supabase.storage.from('p2b').getPublicUrl(path);
      const newLogoUrl = `${urlData.publicUrl}?t=${Date.now()}`;

      const { error: updateError } = await supabase.rpc('update_my_logo_url' as any, { p_logo_url: newLogoUrl });
      if (updateError) throw new Error(updateError.message);

      setLogoUrl(newLogoUrl);
      invalidateProfile();
      await refreshUserData();
      toast({ title: t('brand.messages.logoUpdated'), description: t('brand.messages.logoUpdatedDesc') });
      return newLogoUrl;
    } catch (err: any) {
      toast({ title: t('brand.messages.uploadError'), description: err.message, variant: 'destructive' });
      throw err;
    }
  };

  const handleLogoSmallUpload = async (file: File): Promise<string> => {
    if (!userData?.id) throw new Error('User ID not available');
    try {
      const ext = file.name.split('.').pop() || 'png';
      const path = `users/${userData.id}/logo_small.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('p2b')
        .upload(path, file, { upsert: true, contentType: file.type });

      if (uploadError) throw new Error(uploadError.message);

      const { data: urlData } = supabase.storage.from('p2b').getPublicUrl(path);
      const newLogoSmallUrl = `${urlData.publicUrl}?t=${Date.now()}`;

      const { error: updateError } = await supabase.rpc('update_my_logo_small_url' as any, { p_logo_small_url: newLogoSmallUrl });
      if (updateError) throw new Error(updateError.message);

      setLogoSmallUrl(newLogoSmallUrl);
      invalidateProfile();
      await refreshUserData();
      toast({ title: t('brand.messages.logoCompactUpdated'), description: t('brand.messages.logoCompactUpdatedDesc') });
      return newLogoSmallUrl;
    } catch (err: any) {
      toast({ title: t('brand.messages.uploadError'), description: err.message, variant: 'destructive' });
      throw err;
    }
  };

  if (profileLoading) return <BrandPageSkeleton />;

  return (
    <div className="max-w-6xl mx-auto pb-4 space-y-6">
      {agencyId && (
        <p className="text-sm text-gray-500">{t("brand.personalLogoNote")}</p>
      )}

      {/* First row: Professional info, Gallery, Logo */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        {/* Professional info */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="text-xl font-normal flex items-center gap-2">
              <User className="h-4 w-4" />
              {t("brand.generalInfo.title")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="location">
                {t("brand.generalInfo.location")}
              </Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="location"
                  placeholder={t("brand.generalInfo.locationPlaceholder")}
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            {/* <div className="space-y-2">
              <Label htmlFor="specialization">{t('brand.generalInfo.specialization')}</Label>
              <Input
                id="specialization"
                placeholder={t('brand.generalInfo.specializationPlaceholder')}
                value={specialization}
                onChange={(e) => setSpecialization(e.target.value)}
              />
            </div> */}
            <div className="space-y-2">
              <Label htmlFor="years">
                {t("brand.generalInfo.yearsExperience")}
              </Label>
              <Input
                id="years"
                placeholder={t("brand.generalInfo.yearsExperiencePlaceholder")}
                value={yearsExperience}
                onChange={(e) => setYearsExperience(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("brand.destinations.title")}</Label>
              <AutocompleteSelect
                options={COUNTRIES.filter(
                  (c) => !destinationCountries.includes(c.code),
                ).map((country) => ({
                  value: country.code,
                  label: `${country.flag} ${country.name}`,
                }))}
                value={null}
                onValueChange={(value) => {
                  if (value) {
                    addDestinationCountry(value);
                  }
                }}
                placeholder={t("brand.destinations.addDestination")}
                searchPlaceholder={t("brand.destinations.searchPlaceholder")}
                emptyMessage={t("brand.destinations.noResults")}
                triggerClassName="border rounded-xl"
                displayValue={(option) =>
                  option ? option.label : t("brand.destinations.addDestination")
                }
                searchValue={(option) => option.label}
                side="bottom"
                maxHeight="250px"
              />
            </div>
            <div className="space-y-2">
              <p className="text-sm text-gray-500">
                {t("brand.destinations.specializationHelper")}
              </p>
              <div className="flex flex-wrap gap-2">
                {destinationCountries.map((code) => {
                  const country = getCountryByCode(code);
                  if (!country) return null;
                  return (
                    <Badge
                      key={code}
                      variant="secondary"
                      className="flex items-center gap-1 text-sm"
                    >
                      <span>{country.flag}</span>
                      <span>{country.name}</span>
                      <X
                        className="h-3 w-3 cursor-pointer"
                        onClick={() => removeDestinationCountry(code)}
                      />
                    </Badge>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col gap-6 md:col-span-3">
          {/* Gallery */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-normal flex items-center gap-2">
                <Camera className="h-4 w-4" />
                {t("brand.gallery.title")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-3">
                {[0, 1, 2, 3].map((index) => (
                  <div key={index} className="relative">
                    <ImageUpload
                      showUrlInput={false}
                      showUploadMessage={false}
                      image={gallery[index] || null}
                      onImageChange={(url) => {
                        const newGallery = [...gallery];
                        if (url) {
                          newGallery[index] = url;
                        } else {
                          newGallery.splice(index, 1);
                        }
                        setGallery(newGallery.filter(Boolean));
                      }}
                      uploadFn={(file) => handleGalleryUpload(file, index)}
                      heightClass="h-[90px]"
                      disabled={!userData?.id}
                    />
                    {gallery[index] && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="absolute top-2 right-2 h-6 w-6 p-0 bg-red-500 hover:bg-red-600 text-white border-red-500"
                        onClick={() => removeGalleryImage(index)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Logo */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-normal flex items-center gap-2">
                <Palette className="h-4 w-4" />
                {t("brand.logo.title")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-8">
              <div className="flex flex-row items-center justify-around gap-6">
                {/* Full logo */}
                <div className="flex flex-col items-center gap-4">
                  <p className="text-sm font-medium">
                    {t("brand.logo.title")}
                  </p>
                  <ImageUpload
                    showUrlInput={false}
                    showUploadMessage={false}
                    image={logoUrl}
                    onImageChange={setLogoUrl}
                    uploadFn={handleLogoUpload}
                    heightClass="h-[90px] w-[180px]"
                    disabled={!userData?.id}
                  />
                  <p className="text-xs text-gray-500 text-center max-w-[180px]">
                    {t("brand.logo.extendedDescription")}
                  </p>
                </div>

                {/* Small logo */}
                <div className="flex flex-col items-center gap-4">
                  <p className="text-sm font-medium">
                    {t("brand.logo.titleCompact")}
                  </p>
                  <ImageUpload
                    showUrlInput={false}
                    showUploadMessage={false}
                    image={logoSmallUrl}
                    onImageChange={setLogoSmallUrl}
                    uploadFn={handleLogoSmallUpload}
                    heightClass="h-[90px] w-[90px]"
                    disabled={!userData?.id}
                  />
                  <p className="text-xs text-gray-500 text-center max-w-[180px]">
                    {t("brand.logo.compactDescription")}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Bio */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl font-normal flex items-center gap-2">
            <User className="h-4 w-4" />
            {t("brand.presentation.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="headline">{t("brand.presentation.headline")}</Label>
            <Input
              id="headline"
              placeholder={t("brand.presentation.headlinePlaceholder")}
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
            />
            <p className="text-xs text-gray-500">
              {t("brand.presentation.headlineHelper")}
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="bio">{t("brand.presentation.bio")}</Label>
            <Textarea
              id="bio"
              placeholder={t("brand.presentation.bioPlaceholder")}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={5}
              className="resize-none"
            />
            <p className="text-xs text-gray-500">
              {t("brand.presentation.bioHelper")}
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        {/* Social links */}
        <Card className="md:col-span-3">
          <CardHeader>
            <CardTitle className="text-xl font-normal flex items-center gap-2">
              <Globe className="h-4 w-4" />
              {t("brand.social.title")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="instagram">{t("brand.social.instagram")}</Label>
                <Input
                  id="instagram"
                  placeholder={t("brand.social.instagramPlaceholder")}
                  value={socialInstagram}
                  onChange={(e) => setSocialInstagram(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="linkedin">{t("brand.social.linkedin")}</Label>
                <Input
                  id="linkedin"
                  placeholder={t("brand.social.linkedinPlaceholder")}
                  value={socialLinkedin}
                  onChange={(e) => setSocialLinkedin(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="website">{t("brand.social.website")}</Label>
                <Input
                  id="website"
                  placeholder={t("brand.social.websitePlaceholder")}
                  value={socialWebsite}
                  onChange={(e) => setSocialWebsite(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="facebook">{t("brand.social.facebook")}</Label>
                <Input
                  id="facebook"
                  placeholder={t("brand.social.facebookPlaceholder")}
                  value={socialFacebook}
                  onChange={(e) => setSocialFacebook(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Languages & Certifications */}
        <div className="flex flex-col gap-6 md:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-normal flex items-center gap-2">
                <Languages className="h-4 w-4" />
                {t("brand.languages.title")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder={t("brand.languages.placeholder")}
                  value={newLanguage}
                  onChange={(e) => setNewLanguage(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && (e.preventDefault(), addLanguage())
                  }
                />
                <Button onClick={addLanguage} variant="outline">
                  {t("brand.languages.add")}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {languages.map((item) => (
                  <Badge
                    key={item}
                    variant="secondary"
                    className="flex items-center gap-1"
                  >
                    {item}
                    <X
                      className="h-3 w-3 cursor-pointer"
                      onClick={() => removeLanguage(item)}
                    />
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-normal flex items-center gap-2">
                <Award className="h-4 w-4" />
                {t("brand.certifications.title")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder={t("brand.certifications.placeholder")}
                  value={newCertification}
                  onChange={(e) => setNewCertification(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" &&
                    (e.preventDefault(), addCertification())
                  }
                />
                <Button onClick={addCertification} variant="outline">
                  {t("brand.certifications.add")}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {certifications.map((item) => (
                  <Badge
                    key={item}
                    variant="secondary"
                    className="flex items-center gap-1"
                  >
                    {item}
                    <X
                      className="h-3 w-3 cursor-pointer"
                      onClick={() => removeCertification(item)}
                    />
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Save button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSaveProfile}
          disabled={saving || profileLoading}
          size="lg"
        >
          {saving ? t("brand.saving") : t("brand.saveChanges")}
        </Button>
      </div>
    </div>
  );
};

export default BrandPage;
